<template>
  <Flex>
    <Flex
      v-if="editIconToShow()"
      :class="{ 'router-link-exact-active': isApplied() && this.$route.query.calculated }"
    >
      <input
        v-if="!usedAlone"
        type="checkbox"
        class="checkbox"
        :checked="isApplied()"
        :disabled="isOnlySelectedMeasure() || usedAlone"
        @change="onMeasureChange($event)"
      />
      <label :class="isUsedAlone" @click="selectOnlyThisMeasure()">{{
        getMeasureName(calcMeasure)
      }}</label>
    </Flex>
    <Flex absolute right-0 mx-1>
      <Flex v-if="iconToShow === 'add'" add-measure>
        <Icon src="material/add" class="cursor-pointer" @click="openDialog" />
      </Flex>
    </Flex>
    <Flex absolute right-0 mx-2>
      <Flex v-if="editIconToShow()" edit-measure>
        <Icon
          src="material/edit"
          class="cursor-pointer"
          style="font-size: 15px"
          @click="editMeasure"
        />
        <Icon src="material/clear" class="cursor-pointer" @click="removeMeasure" />
      </Flex>
    </Flex>
    <Modal :show="editing" @close="editing = false">
      <Flex column m-3>
        <Flex column>
          <FormField :label="$t('label.calc.name')">
            <TextField :value.sync="calculationName" :required="true" />
          </FormField>

          <Flex wrap mx-4 mb-4>
            <Flex mr-2 my-1 v-for="(measureName, index) in measureNames" :key="index">
              <Button tabindex="-1" readonly @click="addToCalculation(measureName)">{{
                measureName
              }}</Button>
            </Flex>
          </Flex>

          <FormField :label="$t('label.calc.calculation')">
            <TextField
              :value.sync="calculationText"
              :required="true"
              :rules="[(v) => this.validFormula || $t('message.calc.formulaInvalid')]"
              ref="calculationText"
            />
          </FormField>
        </Flex>
        <Flex justify-end mx-4 mb-4>
          <Flex>
            <Button @click="updateMeasure()" color="primary" :disabled="!enableUpdate">{{
              $t('Button.Update')
            }}</Button>
          </Flex>
          <Flex ml-2>
            <Button @click="cancel()" color="primary">{{ $t('Button.Cancel') }}</Button>
          </Flex>
        </Flex>
      </Flex>
    </Modal>
  </Flex>
</template>

<script>
import { mapState } from 'vuex'
import Button from '@wizer/core/components/button/Button'
import Modal from '@wizer/core/components/modal/Modal'
import Flex from '@wizer/core/components/flex/Flex'
import FormField from '@wizer/core/components/form/FormField'
import TextField from '@wizer/core/components/text-field/TextField'
import Icon from '@wizer/core/components/icon/Icon'
import { compact, isEqual, sortBy, uniqueId } from 'lodash'
import MeasureCalculationMixin from '@/mixins/MeasureCalculationMixin'

export default {
  name: 'CalculatedMeasureSelector',
  mixins: [MeasureCalculationMixin],
  components: {
    Button,
    Modal,
    Flex,
    FormField,
    Icon,
    TextField,
  },
  props: {
    iconToShow: String,
    calcMeasure: Object,
    usedAlone: Boolean,
  },

  data() {
    return {
      checkboxId: uniqueId(),
      editing: false,
      calculationName: '',
      calculationText: '',
      calculationValue: '',
    }
  },

  computed: {
    ...mapState({
      allMeasures: (state) => state.ChartCategory.measures,
      allCalculatedMeasures: (state) => state.ChartCategory.allCalculatedMeasures,
    }),
    isUsedAlone() {
      return {
        'measure-link': true,
        'pl-2': !this.usedAlone,
      }
    },
    enableUpdate() {
      return this.validFormula && this.calculationName
    },
  },

  methods: {
    getMeasureName(calcMeasure) {
      return calcMeasure && calcMeasure.calculationName
    },
    getMeasureNameFromRoute(measure) {
      return measure && measure.split('=')[0]
    },
    isApplied() {
      const calcMeasures = compact([].concat(this.$route.query.calculatedMeasures))
      return calcMeasures.some((measure) => {
        return this.getMeasureNameFromRoute(measure) === this.calcMeasure.calculationName
      })
    },
    isOnlySelectedMeasure() {
      const combinedMeasures = compact(
        [].concat(this.$route.query.measures).concat(this.$route.query.calculatedMeasures),
      )
      return this.isApplied() && combinedMeasures.length === 1
    },
    onMeasureChange(event) {
      const replace = event.target.checked
      const measureToRemove = replace ? null : this.calcMeasure
      this.onMeasureCheck(this.calcMeasure, measureToRemove, replace)
    },
    onMeasureCheck(calcMeasure, measureToRemove, replace) {
      let newCalculatedMeasures = compact([].concat(this.$route.query.calculatedMeasures))
      if (measureToRemove) {
        newCalculatedMeasures = newCalculatedMeasures.filter((measure) => {
          return this.getMeasureNameFromRoute(measure) !== this.getMeasureName(measureToRemove)
        })
      }
      if (replace) {
        newCalculatedMeasures = newCalculatedMeasures.concat(calcMeasure.calculationValue)
      }
      this.loadNewMeasures(newCalculatedMeasures, this.$route.query.measures)
    },
    loadNewMeasures(newCalculatedMeasures, measures) {
      const route = this.getRoute(this.sortedMeasures(newCalculatedMeasures), measures)
      if (!isEqual(this.$route.query, route.query)) {
        this.$router.push(route)
      }
    },
    selectOnlyThisMeasure() {
      this.loadNewMeasures([this.calcMeasure.calculationValue], [])
    },
    sortedMeasures(newCalculatedMeasures) {
      const measureNames = this.allCalculatedMeasures.map((measure) => {
        return this.getMeasureName(measure)
      })
      return sortBy(newCalculatedMeasures, (measure) =>
        measureNames.indexOf(this.getMeasureNameFromRoute(measure)),
      )
    },
    getRoute(newCalculatedMeasures, measures) {
      const newMeasures = compact([].concat(newCalculatedMeasures))
      let selectedSeries = this.$route.query.selectedSeries
      if (newMeasures.length === 1) {
        selectedSeries = ''
      }
      const route = {
        name: this.$route.name,
        query: {
          ...this.$route.query,
          calculatedMeasures: newCalculatedMeasures,
          measures,
          selectedSeries,
          calculated: this.usedAlone,
        },
      }
      return route
    },
    editIconToShow() {
      return this.iconToShow === 'edit' && this.calcMeasure
    },
    removeMeasure() {
      this.allCalculatedMeasures.splice(this.allCalculatedMeasures.indexOf(this.calcMeasure), 1)
      if (this.isApplied()) {
        this.onMeasureCheck(null, this.calcMeasure, false)
      }
    },
    editMeasure() {
      this.editing = true
      this.calculationName = this.calcMeasure.calculationName
      this.calculationText = this.calcMeasure.calculationText
    },
    openDialog() {
      this.editing = true
      this.calculationName = ''
      this.calculationText = ''
    },
    cancel() {
      this.editing = false
    },
    updateMeasure() {
      this.editing = false
      if (!this.calculationName || !this.calculationText) {
        return
      }
      const newMeasure = {
        id: uniqueId(),
        calculationName: this.calculationName,
        calculationText: this.calculationText,
        calculationValue: `${this.calculationName}=${this.calculation()}`,
      }
      if (this.iconToShow === 'add') {
        this.allCalculatedMeasures.push(newMeasure)
      } else if (this.iconToShow === 'edit') {
        const currentIndex = this.allCalculatedMeasures.findIndex(
          (measure) => measure.id === this.calcMeasure.id,
        )
        const oldMeasure = this.allCalculatedMeasures[currentIndex]
        this.allCalculatedMeasures.splice(currentIndex, 1, newMeasure)
        this.onMeasureCheck(newMeasure, oldMeasure, true)
      }
    },
    addToCalculation(measureName) {
      this.$nextTick(() => {
        this.$refs.calculationText.$el.children[0].focus()
      })

      let shouldValidate = true
      let padding = ' '
      if (this.calculationText === '') {
        shouldValidate = false
        padding = ''
      }

      this.calculationText += `${padding}[${measureName}]`

      if (shouldValidate) {
        this.$refs.calculationText.validate()
      }
    },
  },
}
</script>

<style lang="postcss" scoped>
.measure-link {
  @apply break-words text-primary-500;
}
.checkbox {
  margin-top: 0.1rem;
}
.options-header .add-measure,
.options-item .edit-measure {
  visibility: hidden;
}
.options-header:hover .add-measure,
.options-item:hover .edit-measure {
  visibility: visible;
}
.add-measure,
.edit-measure {
  @apply text-gray-600 fill-current;
}
</style>
